import { useRef, useState } from "react";
import { toPng } from "html-to-image";
import { motion, AnimatePresence } from "framer-motion";
import { X, Download, Share2, Sparkles, Flame, Trophy, Award } from "lucide-react";
import BrainLogo from "@/components/BrainLogo";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * ShareCardDialog — renders a beautiful 1080×1080 social card from inside React,
 * then exports it via html-to-image (no LLM, no server, fully client-side).
 *
 * type: "streak" | "level" | "award" | "score"
 * data: { value, label, sub } (sub line e.g. "best yet", "+50 XP")
 * user: { full_name }
 */
export default function ShareCardDialog({ open, onClose, type, data, user }) {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleDownload = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: "#0A0A0B",
      });
      const link = document.createElement("a");
      link.download = `Studivault-${type}-${data.value || "card"}.png`;
      link.href = dataUrl;
      link.click();
      toast.success("Saved to your device");
      api.post("/events/share").catch(() => {});
    } catch (e) {
      toast.error("Couldn't generate image");
    } finally {
      setBusy(false);
    }
  };

  const handleShare = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: "#0A0A0B",
      });
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      const file = new File([blob], `Studivault-${type}.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: `My Studivault ${type}`,
          text: data.tagline || "Studying on Studivault — studivault.com",
        });
        api.post("/events/share").catch(() => {});
      } else {
        // fallback to download
        await handleDownload();
      }
    } catch (e) {
      // user canceled or unsupported
      if (e?.name !== "AbortError") {
        toast.error("Sharing not supported — saved as PNG instead");
        await handleDownload();
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-md flex items-center justify-center p-5"
          onClick={onClose}
          data-testid="share-card-dialog"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 12 }}
            transition={{ type: "spring", stiffness: 280, damping: 22 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-[460px]"
          >
            <button
              onClick={onClose}
              className="absolute -top-12 right-0 w-9 h-9 rounded-full bg-white/8 hover:bg-white/15 text-white flex items-center justify-center"
              data-testid="share-card-close"
              aria-label="Close"
            >
              <X size={16} />
            </button>

            {/* Visible scaled preview */}
            <div className="rounded-3xl overflow-hidden" style={{ boxShadow: "0 30px 80px -20px rgba(124,92,255,0.55), 0 0 60px -10px rgba(90,200,250,0.30)" }}>
              <ShareCard innerRef={cardRef} type={type} data={data} user={user} />
            </div>

            <div className="mt-5 flex items-center gap-2.5" data-testid="share-card-actions">
              <button
                onClick={handleShare}
                disabled={busy}
                className="btn btn-primary flex-1 py-3 text-[14px]"
                data-testid="share-card-share-btn"
              >
                <Share2 size={14} /> Share
              </button>
              <button
                onClick={handleDownload}
                disabled={busy}
                className="px-4 py-3 rounded-full bg-white/8 hover:bg-white/15 text-white text-[13px] font-semibold flex items-center gap-2 transition-colors"
                data-testid="share-card-download-btn"
              >
                <Download size={14} /> Save
              </button>
            </div>
            <p className="text-[12px] text-[#8E8B99] text-center mt-3">
              {busy ? "Rendering…" : "1080 × 1080 · perfect for Instagram, X, anywhere"}
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ----- Card itself -----

const TYPE_CONFIG = {
  streak: {
    icon: Flame,
    eyebrow: "STREAK",
    headline: (v) => `${v}-day streak`,
    tagline: "Studying every day on Studivault.",
    accent1: "#7C5CFF",
    accent2: "#5AC8FA",
  },
  level: {
    icon: Trophy,
    eyebrow: "LEVELED UP",
    headline: (v) => `Level ${v}`,
    tagline: "Climbing the ranks on Studivault.",
    accent1: "#5AC8FA",
    accent2: "#7C5CFF",
  },
  award: {
    icon: Award,
    eyebrow: "ACHIEVEMENT UNLOCKED",
    headline: (v) => v,
    tagline: "Unlocked on Studivault.",
    accent1: "#7C5CFF",
    accent2: "#B8A4FF",
  },
  score: {
    icon: Sparkles,
    eyebrow: "QUIZ RESULT",
    headline: (v) => `${v}%`,
    tagline: "Quiz aced on Studivault.",
    accent1: "#5AC8FA",
    accent2: "#7C5CFF",
  },
  pack: {
    icon: Sparkles,
    eyebrow: "STUDY PACK",
    headline: (v) => v,
    tagline: "Crafted with AI on Studivault.",
    accent1: "#7C5CFF",
    accent2: "#5AC8FA",
  },
};

export function ShareCard({ innerRef, type, data, user }) {
  const cfg = TYPE_CONFIG[type] || TYPE_CONFIG.streak;
  const Icon = cfg.icon;
  const headline = cfg.headline(data.value);

  return (
    <div
      ref={innerRef}
      className="relative aspect-square w-full overflow-hidden"
      style={{
        background: "#0A0A0B",
        fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
      }}
      data-testid="share-card-canvas"
    >
      {/* Ambient gradient blobs */}
      <div
        style={{
          position: "absolute", top: "-20%", left: "-10%", width: "70%", height: "70%",
          background: `radial-gradient(circle, ${cfg.accent1}55, transparent 65%)`,
          filter: "blur(60px)", pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute", bottom: "-25%", right: "-15%", width: "75%", height: "75%",
          background: `radial-gradient(circle, ${cfg.accent2}55, transparent 65%)`,
          filter: "blur(60px)", pointerEvents: "none",
        }}
      />
      {/* Grid texture */}
      <div
        style={{
          position: "absolute", inset: 0,
          backgroundImage: "linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)",
          backgroundSize: "32px 32px",
          maskImage: "radial-gradient(ellipse at center, black 30%, transparent 80%)",
          WebkitMaskImage: "radial-gradient(ellipse at center, black 30%, transparent 80%)",
          pointerEvents: "none",
        }}
      />

      {/* Content — proper 3-row layout */}
      <div className="relative z-10 flex flex-col h-full" style={{ padding: "44px 44px 36px" }}>
        {/* Top — brand */}
        <div className="flex items-center gap-2.5">
          <BrainLogo size={32} />
          <span className="text-[20px] font-bold tracking-tight text-white">Studivault</span>
        </div>

        {/* Middle — headline */}
        <div className="flex-1 flex flex-col items-center justify-center text-center">
          {/* Glowing icon disc */}
          <div className="relative" style={{ width: 72, height: 72, marginBottom: 20 }}>
            <div
              style={{
                position: "absolute", inset: -8, borderRadius: "50%",
                background: `radial-gradient(circle, ${cfg.accent1}80, transparent 65%)`,
                filter: "blur(14px)",
              }}
            />
            <div
              style={{
                position: "relative", width: 72, height: 72, borderRadius: "50%",
                background: `linear-gradient(135deg, ${cfg.accent1}, ${cfg.accent2})`,
                boxShadow: `0 0 32px ${cfg.accent1}99, inset 0 1px 0 rgba(255,255,255,0.3)`,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <Icon size={32} color="#FFFFFF" strokeWidth={2.2} />
            </div>
          </div>

          <div
            style={{
              fontSize: 11, letterSpacing: "0.20em", fontWeight: 800, marginBottom: 10,
              color: cfg.accent1,
              textShadow: `0 0 14px ${cfg.accent1}88`,
            }}
          >
            {cfg.eyebrow}
          </div>

          <div
            className="font-bold tracking-tight"
            style={{
              fontSize: type === "award" ? 32 : (String(headline).length > 12 ? 44 : 64),
              lineHeight: 1.05,
              background: `linear-gradient(135deg, #FFFFFF 0%, ${cfg.accent2} 100%)`,
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
              WebkitTextFillColor: "transparent",
              maxWidth: "100%",
              wordBreak: "break-word",
              filter: `drop-shadow(0 0 20px ${cfg.accent1}66)`,
              padding: "0 4px",
            }}
          >
            {headline}
          </div>

          {data.sub && (
            <div style={{ fontSize: 13, color: "rgba(255,255,255,0.78)", marginTop: 14, fontWeight: 500 }}>
              {data.sub}
            </div>
          )}

          <div style={{ fontSize: 11.5, color: "#A1A1AA", marginTop: 14, maxWidth: 260 }}>
            {cfg.tagline}
          </div>
        </div>

        {/* Bottom — signature */}
        <div className="flex flex-col items-center">
          <div style={{ fontSize: 12.5, color: "rgba(255,255,255,0.7)", fontWeight: 500 }}>
            @{(user?.full_name || "you").split(" ")[0].toLowerCase()}
          </div>
          <div style={{ fontSize: 10, color: "#71717A", marginTop: 4, letterSpacing: "0.2em", textTransform: "uppercase" }}>
            studivault.com
          </div>
        </div>
      </div>
    </div>
  );
}
